import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import React from "react";
import { temp, ui } from "../../interfaces/interfaces";
import Paper from "@mui/material/Paper";
import useDebugInformation from "../../hooks/useDebugInformation";
import { ContactFields, ContactPrefix } from "@utils/BasicTypes";
import ContactUtils from "@utils/contact";
import { ContactFromAPI, ContactFormFields } from "@utils/contact/model";
import { useAppSelector } from "@store/hooks";

type Props = {
  prefix: ContactPrefix,
}

type HandleSave = {
  (selectedContact: ContactFromAPI | undefined, prefix: Props["prefix"]): void
}

const handleSave: HandleSave = (selectedContact, prefix) => {
  if (selectedContact) {
    const formContact = ContactUtils.mapContactResourceToFormContact(selectedContact)
    for (const key in formContact) {
      const field = formContact[key as keyof ContactFormFields]
      if (field) {
        ui[`${prefix}${(key as ContactFields)}`] = field as never;
      }
      else (ui[`${prefix}${(key as ContactFields)}`] as string) = ""
    }
    ui[`${prefix}ModalOpen`] = false
  }

};

const contactsIsOptionEqualToValue = (option: ContactFromAPI, value: ContactFromAPI) => option.contactString === value.contactString ? true : false
const contactsGetOptionLabel = (option: ContactFromAPI): string => option.contactString ? option.contactString : ""
const contactsOnChange = (event: React.SyntheticEvent<Element, Event>, value: ContactFromAPI | undefined, prefix: Props["prefix"]) => ui[`${prefix}SelectedContact`] = value
const contactsGroupBy = (option: ContactFromAPI): string => String(option.scope)
const contactsOptions = (contacts: ContactFromAPI[]): ContactFromAPI[] => contacts.length > 0 ? contacts.map(a => { if (a?.scope) return a; else a.scope = "Search:"; return a }) : []

const ContactModal: React.FC<Props> = ({ prefix }) => {
  const contacts = useAppSelector(({ temp }) => temp[`${prefix}Contacts`])
  const memoProps = React.useMemo(() => ({ mergedContacts: ContactUtils.mergeContacts({ ui, temp, prefix }), contacts, prefix }), [contacts, prefix])
  const modal = useAppSelector(({ ui }) => ui[`${prefix}ModalOpen`])
  const selectedContact = useAppSelector(({ ui }) => ui[`${prefix}SelectedContact`])

  useDebugInformation("ContactModal", { modal, selectedContact, memoProps })
  return (
    <Dialog open={modal} onClose={React.useCallback(() => ui[`${memoProps.prefix}ModalOpen`] = false, [memoProps.prefix])} aria-labelledby="form-dialog-title" maxWidth="xl">
      <DialogTitle id="form-dialog-title">Select stored contact</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>Select valid contact and click save or cancel to enter manually.</DialogContentText>
        <Paper elevation={4}>
          {memoProps.mergedContacts?.length > 0 && (
            <Autocomplete
              id="combo-box-contacts"
              options={contactsOptions(memoProps.mergedContacts)}
              getOptionLabel={contactsGetOptionLabel}
              onChange={(e, v) => contactsOnChange(e, v || undefined, memoProps.prefix)}
              isOptionEqualToValue={contactsIsOptionEqualToValue}
              groupBy={contactsGroupBy}
              value={selectedContact ? selectedContact : null}
              renderInput={(params) => <TextField {...params} label="contact list" variant="outlined" />}
              sx={{ mt: 2 }}
            />
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={React.useCallback(() => ui[`${memoProps.prefix}ModalOpen`] = false, [memoProps.prefix])} color="secondary">
          Cancel
        </Button>
        <Button onClick={React.useCallback(() => handleSave(selectedContact, memoProps.prefix), [memoProps.prefix, selectedContact])} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContactModal